import { ECodeType, ELinkType } from '@app/swagger-types';
import { sampleDataByType } from './csv-examples';
import { csvFieldMapsByType, csvHeadersByType } from './csv-mappers';
import { QRCodeArraySchema, QRCodeFormSchema } from '@app/domain/qr-code/api/qr-code.form.schema.api';
import { ValidationError } from 'yup';

export const downloadSampleFile = (type: ECodeType) => {
  const getSampleData = sampleDataByType[type];

  if (!getSampleData) {
    console.warn(`No sample data defined for type: ${type}`);
    return;
  }

  const sampleData = getSampleData();
  const blob = new Blob([sampleData], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = `sample-${type.toLowerCase()}-bulk.csv`;
  link.click();

  URL.revokeObjectURL(url);
};

export type CsvParseResult = {
  file: File | null;
  data: QRCodeFormSchema[] | null;
  type: ECodeType | null;
  error: string | null;
};

export const handleFileDrop = async (
  file: File,
  expectedType: ECodeType,
  baseQrCode?: QRCodeFormSchema
): Promise<CsvParseResult> => {
  let result: CsvParseResult = {
    file,
    data: null,
    type: null,
    error: null,
  };

  try {
    const text = await file.text();
    const parsed = safeCsvToJson(text);

    if (!parsed.success) {
      result.error = parsed.error;
      return result;
    }

    const data = parsed.data;

    if (!data.length) {
      result.error = 'CSV contains no data rows.';
      return result;
    }

    const headers = Object.keys(data[0] || {});
    const detectedType = detectCodeTypeFromCsvHeaders(headers);

    if (!detectedType) {
      result.error = 'CSV does not match any supported QR code type. Check column headers.';
      return result;
    }

    if (detectedType !== expectedType) {
      return {
        file,
        data: null,
        type: detectedType,
        error: `The CSV type does not match the selected QR code type. Expected "${expectedType}", but received "${detectedType}"`,
      };
    }

    const fieldMap = csvFieldMapsByType[detectedType];

    if (!fieldMap) {
      result.error = `Field mapping not defined for type: ${detectedType}`;
      return result;
    }

    const namesError = validateUniqueNames(data);
    if (namesError) {
      result.error = namesError;
      result.type = detectedType;
      return result;
    }

    const transformedData: Partial<QRCodeFormSchema>[] = [];

    for (let index = 0; index < data.length; index++) {
      const row = data[index];

      const requiredError = validateRequiredFields(row, detectedType);
      if (requiredError) {
        result.error = `Row ${index + 2}: ${requiredError}`;
        result.type = detectedType;
        return result;
      }

      if (detectedType === ECodeType.GS1) {
        const linksError = validateLinkIndices(row);
        if (linksError) {
          result.error = `Row ${index + 2}: ${linksError}`;
          result.type = detectedType;
          return result;
        }
      }

      let mappedRow: Partial<QRCodeFormSchema> = mapKeys(row, fieldMap);

      mappedRow = processTypeSpecificFields(mappedRow, detectedType);

      if (baseQrCode) {
        mappedRow = mergeWithBase(mappedRow, baseQrCode);
      }

      transformedData.push(mappedRow);
    }

    try {
      await QRCodeArraySchema.validate(transformedData, { abortEarly: false });
    } catch (error) {
      if (error instanceof ValidationError) {
        const errorMessages: string[] = error.inner.map((e) => {
          const errorPath = e.path || '';

          const match = errorPath.match(/\[(\d+)\]/);
          const rowIndex = match ? parseInt(match[1], 10) : 0;

          const fieldPath = errorPath.replace(/^\[\d+\]\.?/, '') || '(unknown field)';
          return `Row ${rowIndex + 2}: ${fieldPath} → ${e.message}`;
        });

        result.error = errorMessages.join('\n');
        result.type = detectedType;
        return result;
      }
      throw error;
    }

    result = {
      file,
      data: transformedData as QRCodeFormSchema[],
      type: detectedType,
      error: null,
    };
  } catch (error) {
    result.error = (error as Error).message;
  }

  return result;
};

export const validateUniqueNames = (rows: Record<string, string>[]): string | null => {
  const names = rows.map((row) => row['Name']?.trim()).filter(Boolean);
  const uniqueNames = new Set(names);

  if (uniqueNames.size !== names.length) {
    const nameCounts: Record<string, number> = {};
    const duplicates: string[] = [];

    for (const name of names) {
      nameCounts[name] = (nameCounts[name] || 0) + 1;
      if (nameCounts[name] === 2) {
        duplicates.push(name);
      }
    }

    return `Duplicate name found: "${duplicates[0]}"`;
  }

  return null;
};

export const validateRequiredFields = (row: Record<string, string>, type: ECodeType): string | null => {
  if (!row['Name'] || !row['Name'].trim()) {
    return 'Required field "Name" is empty';
  }

  switch (type) {
    case ECodeType.WEBSITE:
      if (!row['Website URL'] || !row['Website URL'].trim()) {
        return 'Required field "Website URL" is empty';
      }
      break;

    case ECodeType.GS1:
      if (!row['GTIN (EAN) Code'] || !row['GTIN (EAN) Code'].trim()) {
        return 'Required field "GTIN (EAN) Code" is empty';
      }
      if (!row['Title'] || !row['Title'].trim()) {
        return 'Required field "Title" is empty';
      }
      break;
  }

  return null;
};

export const validateLinkIndices = (row: Record<string, string>): string | null => {
  const linkPairs: { index: number; title: boolean; url: boolean; linkType: ELinkType }[] = [];

  for (let i = 1; i <= 10; i++) {
    const titleKey = `Link ${i} title`;
    const urlKey = `Link ${i} URL`;

    const title = !!(row[titleKey] && row[titleKey].trim());
    const url = !!(row[urlKey] && row[urlKey].trim());

    if (title || url) {
      linkPairs.push({ index: i, title, url, linkType: ELinkType.CUSTOM });
    }
  }

  let lastIndex = 0;
  for (const pair of linkPairs) {
    if (pair.index !== lastIndex + 1) {
      return `Invalid link sequence: missing link ${lastIndex + 1} but found link ${pair.index}`;
    }
    lastIndex = pair.index;

    if (pair.title && !pair.url) {
      return `Link ${pair.index} has a title but no URL`;
    }
    if (!pair.title && pair.url) {
      return `Link ${pair.index} has a URL but no title`;
    }
  }

  return null;
};

export const mapKeys = (input: Record<string, string>, fieldMap: Record<string, string>): Partial<QRCodeFormSchema> => {
  const result: Partial<QRCodeFormSchema> = {};

  for (const key in input) {
    if (fieldMap[key]) {
      const newKey = fieldMap[key];
      const value = input[key].trim();

      setNestedValue(result, newKey, value);
    }
  }

  return result;
};

export const setNestedValue = (obj: Record<string, any>, path: string, value: any): void => {
  if (!path.includes('.') && !path.includes('[')) {
    obj[path] = value;
    return;
  }

  const parts = path.split('.');
  let current = obj;

  for (let i = 0; i < parts.length - 1; i++) {
    const part = parts[i];

    if (part.includes('[') && part.includes(']')) {
      const arrayName = part.substring(0, part.indexOf('['));
      const indexStr = part.substring(part.indexOf('[') + 1, part.indexOf(']'));
      const index = parseInt(indexStr, 10);

      if (!current[arrayName]) {
        current[arrayName] = [];
      }
      while (current[arrayName].length <= index) {
        current[arrayName].push({});
      }
      current = current[arrayName][index];
    } else {
      if (!current[part]) {
        current[part] = {};
      }
      current = current[part];
    }
  }

  const lastKey = parts[parts.length - 1];
  current[lastKey] = value;
};

export const processTypeSpecificFields = (
  row: Partial<QRCodeFormSchema>,
  type: ECodeType
): Partial<QRCodeFormSchema> => {
  const processed = { ...row };

  if (type === ECodeType.GS1 && processed.gs1?.customLinks) {
    processed.gs1.customLinks = processed.gs1.customLinks.filter((link: any) => link && (link.title || link.url));

    processed.gs1.customLinks.forEach((link: any, i: number) => {
      link.index = i;
      link.linkType = ELinkType.CUSTOM;
    });
  }

  return processed;
};

export const mergeWithBase = (row: Record<string, any>, base: Record<string, any>): Record<string, any> => {
  const result = JSON.parse(JSON.stringify(base));

  function deepMerge(target: any, source: any) {
    for (const key in source) {
      const value = source[key];

      if (value === null || value === undefined || value === '') {
        continue;
      }

      if (value && typeof value === 'object' && !Array.isArray(value)) {
        if (!target[key] || typeof target[key] !== 'object') {
          target[key] = {};
        }
        deepMerge(target[key], value);
      } else if (Array.isArray(value)) {
        target[key] = [...value];
      } else {
        target[key] = value;
      }
    }
  }

  deepMerge(result, row);
  return result;
};

const safeCsvToJson = (
  csvText: string
): { success: true; data: Record<string, string>[] } | { success: false; error: string } => {
  try {
    const lines = csvText.trim().split('\n');
    if (lines.length < 2) {
      return { success: false, error: 'CSV must include a header and at least one row of data.' };
    }

    const headers = lines[0].split(',').map((h) => h.trim());

    const data = lines.slice(1).map((line) => {
      const values = line.split(',').map((v) => v.trim());

      const obj: Record<string, string> = {};
      headers.forEach((header, i) => {
        obj[header] = values[i] || '';
      });

      return obj;
    });

    if (data.length === 0) {
      return { success: false, error: 'CSV contains no data rows.' };
    }

    return { success: true, data };
  } catch (err) {
    return { success: false, error: (err as Error).message };
  }
};

export const detectCodeTypeFromCsvHeaders = (headers: string[]): ECodeType | null => {
  for (const [type, requiredHeaders] of Object.entries(csvHeadersByType)) {
    const allHeadersExist = requiredHeaders.every((h) => headers.includes(h));
    if (allHeadersExist) {
      return type as ECodeType;
    }
  }
  return null;
};
